import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FragnoModal, FragnoButton } from 'fragno/react';
import '../../style/components/profile/WalletModal.scss';

// Ensure the body scroll-lock class is removed when this modal unmounts.
const FRAGNO_SCROLL_LOCK = 'fragno-modal-scroll-lock';

const WalletModal = ({
  isOpen,
  onClose,
  primaryButton,
  secondaryButton,
  showCloseButton = true,
  closeOnBackdropClick = true,
  children,
  ...rest
}) => {
  // Hold a ref to our own fragno-modal element so the unmount cleanup can
  // exclude it from the sibling-open check. Without the exclusion, a stale
  // [open] attribute on the unmounting host (fragno's disconnectedCallback
  // never calls removeAttribute) could match the querySelector and skip
  // the class removal, defeating the fix.
  const modalRef = useRef(null);

  // Fragno's <fragno-modal> watches its own host element with an
  // IntersectionObserver and auto-closes the dialog if that element isn't
  // "intersecting" the viewport (meant to release the scroll lock if the
  // modal scrolls out of view or the tab backgrounds). The host's own light-
  // DOM box collapses to ~zero size — its visible content is a
  // `position: fixed` dialog rendered in shadow DOM, which contributes
  // nothing to the host's layout — while React defers syncing the `open`
  // attribute to a `useEffect` that runs after mount/paint. If the
  // observer's first callback lands after that effect has already called
  // showModal() but still reports the pre-open zero-area reading, it
  // spuriously closes a dialog that only just opened (reproduced on
  // master too, confirmed via node_modules/fragno/src/components/modal/
  // modal.js and the deployed bundle — not something introduced by this
  // PR). Disconnecting the observer right after mount removes the race;
  // the only thing lost is auto-closing on scroll-away/tab-switch, which
  // none of our modals rely on. `intersectionObserver` is a plain (non-
  // private) instance property. Reported to the Fragno team; remove this
  // once fixed upstream.
  useEffect(() => {
    modalRef.current?.el?.intersectionObserver?.disconnect();
  }, []);

  useEffect(() => {
    // Capture at mount time — React nulls modalRef.current before passive
    // effect cleanup runs, so reading it in the cleanup itself yields undefined.
    const ownEl = modalRef.current?.el;
    return () => {
      // If .el is unavailable (e.g. fragno API change), fall back to always
      // removing the lock rather than silently leaving it stuck.
      if (!ownEl) {
        document.body.classList.remove(FRAGNO_SCROLL_LOCK);
        return;
      }
      const others = Array.from(
        document.querySelectorAll('fragno-modal[open]'),
      ).filter((el) => el !== ownEl);
      if (others.length === 0) {
        document.body.classList.remove(FRAGNO_SCROLL_LOCK);
      }
    };
  }, []);

  return (
    <FragnoModal
      ref={modalRef}
      open={isOpen}
      showCloseButton={showCloseButton}
      onClose={onClose}
      closeOnBackdropClick={closeOnBackdropClick}
      {...rest}
    >
      {children}
      {secondaryButton && (
        <FragnoButton
          slot="secondary-action"
          hierarchy="secondary"
          label={secondaryButton.text}
          onClick={secondaryButton.onClick}
          disabled={secondaryButton.disabled}
          loading={secondaryButton.loading}
          iconLeading={secondaryButton.iconLeading}
          iconTrailing={secondaryButton.iconTrailing}
          size="md"
        />
      )}
      {primaryButton && (
        <FragnoButton
          slot="primary-action"
          hierarchy="primary"
          label={primaryButton.text}
          onClick={primaryButton.onClick}
          disabled={primaryButton.disabled}
          loading={primaryButton.loading}
          iconLeading={primaryButton.iconLeading}
          iconTrailing={primaryButton.iconTrailing}
          size="md"
        />
      )}
    </FragnoModal>
  );
};

const buttonShape = PropTypes.shape({
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  iconLeading: PropTypes.string,
  iconTrailing: PropTypes.string,
});

WalletModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  primaryButton: buttonShape,
  secondaryButton: buttonShape,
  showCloseButton: PropTypes.bool,
  closeOnBackdropClick: PropTypes.bool,
  children: PropTypes.node,
};

export default memo(WalletModal);
